import React, { useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import axios from "axios";
import { Image } from "hesus-ui-kit";
import { GridRow, GridCell } from "../../AddEditInvoice.styled";
import infoIcon from "@icons/ic-info-dark-blue.svg";

const RealisedQuantityRow = (props) => {
  const {
    t,
    summary,
    quoteItem,
    showHaulier,
    showCentre,
    gridColumns,
    onChange,
  } = props;

  const {
    needSupplierPlanning,
    needCenter,
    needCarrier,
    needCombination,
    needCombinationCarrier,
    needCombinationCenter,
  } = quoteItem.hesusService;

  const unit = quoteItem?.unit;

  const isInvoiced = !needSupplierPlanning
    ? summary.otherServiceSummary?.invoiced === true
    : needCenter && needCarrier && !needCombination
    ? unit === "LOAD"
      ? summary.supplierPlanningLineSummary?.haulierSummary?.invoiced === true
      : ["M3", "TON"].includes(unit)
      ? summary.supplierPlanningLineSummary?.centreSummary?.invoiced === true
      : false
    : needCenter &&
      (!needCombination || (needCombination && needCombinationCarrier))
    ? summary.supplierPlanningLineSummary?.centreSummary?.invoiced === true
    : needCarrier &&
      (!needCombination || (needCombination && needCombinationCenter))
    ? summary.supplierPlanningLineSummary?.haulierSummary?.invoiced === true
    : false;

  const getRealisedSummary = () => {
    if (!needSupplierPlanning) {
      return summary?.otherServiceSummary;
    }

    if (showCentre && !showHaulier) {
      return summary?.supplierPlanningLineSummary?.centreSummary;
    }

    if (showHaulier && !showCentre) {
      return summary?.supplierPlanningLineSummary?.haulierSummary;
    }

    if (showCentre && showHaulier) {
      return unit === "LOAD"
        ? summary?.supplierPlanningLineSummary?.haulierSummary
        : summary?.supplierPlanningLineSummary?.centreSummary;
    }

    return null;
  };

  const getBackGroundColor = () => {
    const otherServiceSummary = summary?.otherServiceSummary;
    const centreSummary = summary?.supplierPlanningLineSummary?.centreSummary;
    const haulierSummary = summary?.supplierPlanningLineSummary?.haulierSummary;

    // Already invoiced
    if (isInvoiced) {
      return "#EDEDED";
    }

    // Non supplier-planning service
    if (!needSupplierPlanning) {
      if (!otherServiceSummary) {
        return "#FFC8BD";
      }

      return otherServiceSummary.readyForInvoicing ? "#E1FFDE" : "#FFEABC";
    }

    // Centre only
    if (showCentre && !showHaulier) {
      if (!centreSummary) {
        return "#FFC8BD";
      }

      return centreSummary.readyForInvoicing ? "#E1FFDE" : "#FFEABC";
    }

    // Haulier only
    if (showHaulier && !showCentre) {
      if (!haulierSummary) {
        return "#FFC8BD";
      }

      return haulierSummary.readyForInvoicing ? "#E1FFDE" : "#FFEABC";
    }

    // Centre + Haulier
    if (showCentre && showHaulier) {
      if (!centreSummary || !haulierSummary) {
        return "#FFC8BD";
      }

      if (centreSummary.readyForInvoicing && haulierSummary.readyForInvoicing) {
        return "#E1FFDE";
      }

      return "#FFEABC";
    }

    return null;
  };

  const realisedSummary = getRealisedSummary();

  const isDisabled = !isInvoiced && !realisedSummary;

  const handleChange = () => {
    onChange(summary.realisedQuantityId);
  };

  const getRealisedQuantity = () => {
    // other service summary
    if (!needSupplierPlanning) {
      const otherSummary = summary?.otherServiceSummary;

      return otherSummary
        ? { quantity: otherSummary.quantity, unit: otherSummary.unit }
        : null;
    }

    const centreSummary = summary?.supplierPlanningLineSummary?.centreSummary;
    const haulierSummary = summary?.supplierPlanningLineSummary?.haulierSummary;

    // Treatment + Transport together
    if (needCenter && needCarrier && !needCombination) {
      const selectedSummary = unit === "LOAD" ? haulierSummary : centreSummary;

      return selectedSummary
        ? { quantity: selectedSummary.quantity, unit: selectedSummary.unit }
        : null;
    }

    // Treatment only
    if (needCenter && !needCarrier && !needCombination) {
      return centreSummary
        ? { quantity: centreSummary.quantity, unit: centreSummary.unit }
        : null;
    }

    // Transport only
    if (needCarrier && !needCenter && !needCombination) {
      return haulierSummary
        ? { quantity: haulierSummary.quantity, unit: haulierSummary.unit }
        : null;
    }

    // Treatment + Transport separated - Treatment
    if (needCombination && needCenter && needCombinationCarrier) {
      return centreSummary
        ? { quantity: centreSummary.quantity, unit: centreSummary.unit }
        : null;
    }

    // Treatment + Transport separated - Transport
    if (needCombination && needCarrier && needCombinationCenter) {
      return haulierSummary
        ? { quantity: haulierSummary.quantity, unit: haulierSummary.unit }
        : null;
    }

    return null;
  };

  const realisedQty = getRealisedQuantity();

  return (
    <GridRow
      column={gridColumns}
      padding="0"
      gap="0px"
      borderBottom="1px solid #008395"
      borderLeft="1px solid #008395"
      borderRight="1px solid #008395"
      background={getBackGroundColor()}
    >
      <GridCell
        padding="8px"
        color="#0A4986"
        textAlign="center"
      >
        {isInvoiced ? (
          <Image
            src={infoIcon}
            alt={t("INVOICES_SUMMARIES_ALREADY_INVOICED_TOOLTIP")}
            title={t("INVOICES_SUMMARIES_ALREADY_INVOICED_TOOLTIP")}
            className="add-edit-invoice__image-view-icon"
          />
        ) : (
          <input
            type="checkbox"
            checked={!!summary.selected}
            onChange={handleChange}
            disabled={isDisabled}
          />
        )}
      </GridCell>
      <GridCell
        color="#0A4986"
        borderLeft="1px solid #0A4986"
        padding="8px"
        textAlign="center"
      >
        {summary.clientOrderDate}
      </GridCell>
      <GridCell
        borderLeft="1px solid #0A4986"
        color="#0A4986"
        padding="8px"
        textAlign="center"
      >
        {summary.quantityPlanned != null
          ? `${summary.quantityPlanned} ${t(summary.unitPlanned) || ""}`
          : "-"}
      </GridCell>
      {showHaulier && (
        <GridCell borderLeft="1px solid #0A4986" color="#0A4986" padding="8px">
          {summary.supplierPlanningLineSummary?.haulierSummary?.haulierName ||
            summary.suppliersPlanningLines?.find(
              (line) => line?.haulage?.company?.name
            )?.haulage?.company?.name ||
            "-"}
        </GridCell>
      )}
      {showCentre && (
        <GridCell borderLeft="1px solid #0A4986" color="#0A4986" padding="8px">
          {summary.supplierPlanningLineSummary?.centreSummary?.centreName ||
            summary.suppliersPlanningLines?.find(
              (line) => line?.solution?.centre?.name
            )?.solution?.centre?.name ||
            "-"}
        </GridCell>
      )}
      <GridCell
        borderLeft="1px solid #0A4986"
        color="#0A4986"
        padding="8px"
        textAlign="center"
      >
        {realisedQty ? `${realisedQty.quantity} ${t(realisedQty.unit)}` : "-"}
      </GridCell>
    </GridRow>
  );
};

RealisedQuantityRow.propTypes = {
  t: PropTypes.func.isRequired,
};

RealisedQuantityRow.defaultProps = {};
export default withTranslation()(RealisedQuantityRow);
